import { singleton } from "@solvari/utils";

import type {
  AnalyticsMessages,
  FunctionalMessages,
  WindowMessages,
} from "@/lib/helpers/iframes/types";

import { analyticsEventNames } from "@/lib/helpers/iframes/types";

type SolvariEventType = "analytics" | "functional";

type SolvariEvent<
  EventName extends keyof WindowMessages = keyof WindowMessages,
> = WindowMessages[EventName] & {
  solvariEvent: EventName;
  // Absent in messages from iframes built before SG-18863
  solvariEventType?: SolvariEventType;
};

type AnalyticsEvent = AnalyticsMessages[keyof AnalyticsMessages] & {
  solvariEvent: keyof AnalyticsMessages;
  solvariEventType: "analytics";
};

type AnalyticsCatchAllCallback = (
  event: keyof AnalyticsMessages,
  data: AnalyticsMessages[keyof AnalyticsMessages],
) => void;

type WindowMessageContext = {
  iframe?: HTMLIFrameElement;
  window: MessageEventSource;
};

type IframeMessageContext = Required<WindowMessageContext>;

function isCorrectSolvariEvent<EventName extends keyof WindowMessages>(
  event: EventName,
  data: unknown,
): data is SolvariEvent<EventName> {
  return (
    typeof data === "object" &&
    data !== null &&
    "solvariEvent" in data &&
    data.solvariEvent === event
  );
}

function isAnalyticsEvent(data: unknown): data is AnalyticsEvent {
  return analyticsEventNames.some(
    (name) =>
      isCorrectSolvariEvent(name, data) &&
      data.solvariEventType === "analytics",
  );
}

const iframes = singleton(() => {
  // oxlint-disable-next-line unicorn/prefer-query-selector -- live collection needed: singleton caches the result, so a static NodeList would miss lazily-inserted iframes
  return document.getElementsByTagName("iframe");
});

/*
  Register your messages in the global WindowMessages interface to get cross domain type safety<br>
  event: Record<string, unknown>;
 */
function postSolvariEvent(
  event: keyof WindowMessages,
  eventType: SolvariEventType,
  data: WindowMessages[keyof WindowMessages],
  targetWindow: Window,
) {
  targetWindow.postMessage(
    { solvariEvent: event, solvariEventType: eventType, ...data },
    "*",
  );
}

function postWindowMessage<EventName extends keyof FunctionalMessages>(
  event: EventName,
  data: FunctionalMessages[EventName],
  targetWindow: Window,
) {
  postSolvariEvent(event, "functional", data, targetWindow);
}

function emitWindowMessage<EventName extends keyof FunctionalMessages>(
  event: EventName,
  data: FunctionalMessages[EventName],
) {
  postWindowMessage(event, data, window.parent);
}

function emitWindowAnalyticsMessage<EventName extends keyof AnalyticsMessages>(
  event: EventName,
  data: AnalyticsMessages[EventName],
) {
  postSolvariEvent(event, "analytics", data, window.parent);
}

function getSourceIframe(source: MessageEventSource | null) {
  return [...iframes()].find((iframe) => iframe.contentWindow === source);
}

/*
  Register your messages in the global WindowMessages interface to get cross domain type safety<br>
  event: Record<string, unknown>;
 */
function onWindowMessage<EventName extends keyof WindowMessages>(
  event: EventName,
  callback: (
    data: SolvariEvent<EventName>,
    context: WindowMessageContext,
  ) => void,
) {
  window.addEventListener("message", ({ source, data }) => {
    if (!isCorrectSolvariEvent(event, data)) {
      return;
    }

    callback(data, { window: source!, iframe: getSourceIframe(source) });
  });
}

// Callback runs only for messages sent by one of this page's iframes.
function onIframeMessage<EventName extends keyof WindowMessages>(
  event: EventName,
  callback: (
    data: SolvariEvent<EventName>,
    context: IframeMessageContext,
  ) => void,
) {
  onWindowMessage(event, (data, { window: source, iframe }) => {
    if (iframe) {
      callback(data, { window: source, iframe });
    }
  });
}

/*
  Public analytics API: payload only, no envelope keys or iframe context.
  Runtime filter on solvariEventType → affiliates call it from untyped JS.
 */
function onWindowAnalyticsMessage<EventName extends keyof AnalyticsMessages>(
  event: EventName,
  callback: (data: AnalyticsMessages[EventName]) => void,
): void;
function onWindowAnalyticsMessage(callback: AnalyticsCatchAllCallback): void;
function onWindowAnalyticsMessage(
  eventOrCallback: AnalyticsCatchAllCallback | string,
  callback?: (data: AnalyticsMessages[keyof AnalyticsMessages]) => void,
) {
  window.addEventListener("message", ({ data }) => {
    if (!isAnalyticsEvent(data)) {
      return;
    }

    const { solvariEvent, solvariEventType: _eventType, ...payload } = data;
    if (typeof eventOrCallback === "function") {
      eventOrCallback(solvariEvent, payload);
    } else if (solvariEvent === eventOrCallback) {
      callback?.(payload);
    }
  });
}

export type { IframeMessageContext, WindowMessageContext };
export {
  emitWindowAnalyticsMessage,
  emitWindowMessage,
  onIframeMessage,
  onWindowAnalyticsMessage,
  onWindowMessage,
  postWindowMessage,
};
